import React, { useState } from "react";
import clsx from "clsx";
import { PrivacyCenterDto } from "../../../common/service/server/dto/PrivacyCenterDto";
import { PencilIcon } from "@heroicons/react/24/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { LogoUploadDialog } from "../dialog";
import { EXTENDED_FILE_TYPES } from "../dialog/LogoUploadDialog";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Modal, SimpleCheckboxControlled, TextInput, ToolTip } from "@truevault/aquilo";
import { isUrl } from "../../util/validation";
import { removeUrlProtocol } from "../../util/privacyCenterUtil";
import { Form } from "react-final-form";

export function PrivacyCenterHeaderLogoSettingsItem({
  org,
  privacyCenter,
  updatePrivacyCenterValues,
}: {
  org: OrganizationDto;
  privacyCenter: PrivacyCenterDto;
  updatePrivacyCenterValues: (values) => Promise<void>;
}) {
  const [isHeaderLogoDialogOpen, setIsHeaderLogoDialogOpen] = useState(false);

  const hasLogoUrl = privacyCenter?.logoUrl?.length > 0;

  return (
    <div>
      <div className="text-neutral-900 text-[18px] font-semibold">Header Logo Settings</div>
      <div
        className={clsx(
          "w-[256px] h-[100px] mt-[4px] grid place-items-center",
          "border border-solid border-neutral-300 rounded-[8px]",
          { "bg-neutral-0": hasLogoUrl },
          "relative",
        )}
      >
        {hasLogoUrl ? (
          <img src={privacyCenter.logoUrl} className="max-w-[256px] max-h-[100px]" />
        ) : (
          <div className="text-neutral-500 text-[48px] font-bold leading-[28px] tracking-[0]">
            Your Logo
          </div>
        )}
        <div className="absolute top-[74px] right-[-20px]">
          <EditIcon onClick={() => setIsHeaderLogoDialogOpen(true)} />
        </div>
      </div>
      <LogoUploadDialog
        acceptedTypes={EXTENDED_FILE_TYPES}
        logoType="LOGO"
        title="Upload Website Header Logo"
        organizationId={org.id}
        open={isHeaderLogoDialogOpen}
        onClose={() => setIsHeaderLogoDialogOpen(false)}
        onDone={async (logoUrl: string) => {
          await updatePrivacyCenterValues({
            logoUrl,
          });
        }}
        successMessage={undefined}
      />
    </div>
  );
}

export function PrivacyCenterTitleSettingsItem({
  privacyCenter,
  updatePrivacyCenterValues,
}: {
  privacyCenter: PrivacyCenterDto;
  updatePrivacyCenterValues: (values) => Promise<void>;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const [currentTitleText, setCurrentTitleText] = useState(privacyCenter.titleText);

  const hasTitleText = privacyCenter?.titleText?.trim()?.length > 0;

  return (
    <>
      <div className="relative">
        <div
          className={clsx(
            "flex items-center justify-center",
            "h-[56px] px-[24px] bg-neutral-0 rounded-[8px]",
            "border border-solid border-neutral-300",
            "text-neutral-900 text-[40px] font-semibold text-center",
          )}
        >
          {hasTitleText ? privacyCenter.titleText : "Our Privacy Policy"}
        </div>
        <div className="absolute top-[25px] right-[-25px]">
          <EditIcon onClick={() => setIsDialogOpen(true)} />
        </div>
      </div>
      {isDialogOpen && (
        <Modal
          title="Change Policy Title"
          body="This will be the title that will show on top of your privacy policy"
          alignText="left"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => {
            if (isSaving) return;

            setIsDialogOpen(false);
          }}
          secondButton="Save"
          secondButtonColor="primary"
          onSecondButtonClick={async () => {
            if (isSaving) return;

            setIsSaving(true);
            await updatePrivacyCenterValues({
              titleText: currentTitleText,
            });
            setIsSaving(false);
            setIsDialogOpen(false);
          }}
          showOverlay
          closeButton
          onClose={() => {
            if (isSaving) return;

            setIsDialogOpen(false);
          }}
          alignButton="right"
          contentPadding="pt-[24px] px-[24px]"
          buttonPadding="pt-[16px] pr-[24px] pb-[24px]"
        >
          <TextInput
            field="privacy-center-title-text"
            onChange={(e) => {
              setCurrentTitleText(e.target.value);
            }}
            defaultValue={hasTitleText ? privacyCenter.titleText : undefined}
            placeholder="Our Privacy Policy"
            maxLength={90}
          />
        </Modal>
      )}
    </>
  );
}

const defaultWelcomeText =
  "Our Company is committed to your privacy. View our privacy policy to understand how we use your data to enrich your experience or contact us regarding your data. ";

export function PrivacyCenterWelcomeTextSettingsItem({
  privacyCenter,
  updatePrivacyCenterValues,
}: {
  privacyCenter: PrivacyCenterDto;
  updatePrivacyCenterValues: (values) => Promise<void>;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const [currentWelcomeText, setCurrentWelcomeText] = useState(privacyCenter.welcomeText);

  const hasWelcomeText = privacyCenter?.welcomeText?.trim()?.length > 0;

  return (
    <>
      <div className="relative">
        <div
          className={clsx(
            "w-[500px] px-[24px] py-[12px] bg-neutral-0 rounded-[8px]",
            "border border-solid border-neutral-300",
            hasWelcomeText ? "text-neutral-800" : "text-neutral-900",
            "text-[14px] text-center",
          )}
        >
          {hasWelcomeText
            ? privacyCenter.welcomeText
            : "Welcome Text is disabled and won't be visible on your privacy website"}
        </div>
        <div className="absolute bottom-[-19px] right-[-25px]">
          <EditIcon onClick={() => setIsDialogOpen(true)} />
        </div>
      </div>
      {isDialogOpen && (
        <Modal
          title="Welcome Text"
          body="This is the text that will show above your actual policy. Use it to highlight your company's data privacy value in a user friendly way."
          alignText="left"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => {
            if (isSaving) return;

            setIsDialogOpen(false);
          }}
          secondButton="Save"
          secondButtonColor="primary"
          onSecondButtonClick={async () => {
            if (isSaving) return;

            setIsSaving(true);
            await updatePrivacyCenterValues({
              welcomeText: currentWelcomeText,
            });
            setIsSaving(false);
            setIsDialogOpen(false);
          }}
          showOverlay
          closeButton
          onClose={() => {
            if (isSaving) return;

            setIsDialogOpen(false);
          }}
          alignButton="right"
          contentPadding="pt-[24px] px-[24px]"
          buttonPadding="pt-[16px] pr-[24px] pb-[24px]"
        >
          <textarea
            rows={4}
            className={clsx(
              "block w-[500px] rounded-md px-3 py-1.5 mb-[16px] text-[14px] placeholder:text-neutral-600",
              "border border-solid border-neutral-300",
              "resize-none",
              "text-neutral-900",
              "focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none",
            )}
            value={currentWelcomeText}
            onChange={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
              setCurrentWelcomeText(e.target.value);
            }}
          />
          <SimpleCheckboxControlled
            name="enableWelcomeText"
            checked={currentWelcomeText?.length > 0}
            label="Enable Welcome Text"
            onChange={(newChecked) => {
              if (newChecked === true) {
                setCurrentWelcomeText(defaultWelcomeText);
              } else {
                setCurrentWelcomeText("");
              }
            }}
          />
        </Modal>
      )}
    </>
  );
}

export function PrivacyCenterFaviconSettingsItem({
  org,
  privacyCenter,
  updatePrivacyCenterValues,
}: {
  org: OrganizationDto;
  privacyCenter: PrivacyCenterDto;
  updatePrivacyCenterValues: (values) => Promise<void>;
}) {
  const [isFaviconDialogOpen, setIsFaviconDialogOpen] = useState(false);

  const hasFaviconUrl = privacyCenter?.faviconUrl?.length > 0;

  return (
    <div>
      <div className="flex items-center gap-[6px]">
        <div className="text-neutral-700 text-[14px] font-medium">Favicon</div>
        <div className="text-neutral-500 text-[12px] font-medium">(optional)</div>
        <ToolTip text="A favicon is a small logo that appears in the browser tab." width="250px">
          <div>
            <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
          </div>
        </ToolTip>
      </div>
      <div
        className={clsx(
          "w-[42px] h-[42px] mt-[4px] grid place-items-center",
          "border border-solid border-neutral-300 rounded-[8px]",
          { "bg-neutral-0": hasFaviconUrl },
          "relative",
        )}
      >
        {hasFaviconUrl ? (
          <img src={privacyCenter.faviconUrl} className="max-w-[42px] max-h-[42px]" />
        ) : (
          <div className="text-neutral-500 text-[16px] font-bold leading-[24px] tracking-[0]">
            Fav
          </div>
        )}
        <div className="absolute top-[25px] right-[-25px]">
          <EditIcon onClick={() => setIsFaviconDialogOpen(true)} />
        </div>
      </div>
      <LogoUploadDialog
        acceptedTypes={EXTENDED_FILE_TYPES}
        logoType="FAVICON"
        title="Upload Favicon"
        organizationId={org.id}
        open={isFaviconDialogOpen}
        onClose={() => setIsFaviconDialogOpen(false)}
        onDone={async (faviconUrl: string) => {
          await updatePrivacyCenterValues({
            faviconUrl,
          });
        }}
        successMessage={undefined}
      />
    </div>
  );
}

export function PrivacyCenterLogoLinkUrlSettingsItem({
  privacyCenter,
  updatePrivacyCenterValues,
}: {
  privacyCenter: PrivacyCenterDto;
  updatePrivacyCenterValues: (values) => Promise<void>;
}) {
  const [showError, setShowError] = useState(false);

  const hasLogoLinkUrl = privacyCenter?.logoLinkUrl?.length > 0;

  return (
    <Form
      onSubmit={() => {}}
      render={() => {
        return (
          <div>
            <div className="flex items-center gap-[6px]">
              <label className="block text-[14px] font-medium text-gray-700">Website URL</label>
              <ToolTip
                text="This link is where visitors will be directed when they click your logo from within your Privacy Center."
                width="250px"
              >
                <div>
                  <InformationCircleIcon className="w-[16px] h-[16px] text-blue-gray-900" />
                </div>
              </ToolTip>
            </div>
            <TextInput
              field="privacy-center-logo-link-url"
              onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                const newLogoLinkUrl = e.target.value.trim();

                if (isUrl(newLogoLinkUrl)) {
                  setShowError(false);
                  await updatePrivacyCenterValues({
                    logoLinkUrl: removeUrlProtocol(newLogoLinkUrl),
                  });
                } else {
                  setShowError(true);
                }
              }}
              defaultValue={hasLogoLinkUrl ? privacyCenter.logoLinkUrl : undefined}
              placeholder="yourwebsite.com"
              inputWidth="w-[256px]"
              inputTextSize="text-[14px] text-neutral-800"
              inputBorder={showError ? "border border-solid border-red-500" : undefined}
            />
          </div>
        );
      }}
    />
  );
}

export const EditIcon = ({ onClick }: { onClick: () => void }) => {
  return (
    <div
      className={clsx(
        "w-[38px] h-[38px]",
        "bg-white",
        "border border-solid border-neutral-300 rounded-[19px]",
        "grid place-items-center",
        "cursor-pointer",
      )}
      onClick={onClick}
    >
      <PencilIcon className="w-[20px] h-[20px] text-neutral-500" />
    </div>
  );
};
